import { editorClient } from "@/lib/editorClient";
import {
  IconButton,
  Button,
  Alert,
  EditorTopbar,
  DropdownMenu,
} from "@op/sf-editor";
import {
  FeatherArrowRight,
  FeatherArrowUpRight,
  FeatherBookA,
  FeatherBookmark,
  FeatherChevronDown,
  FeatherImage,
  FeatherInfo,
  FeatherSettings,
  FeatherText,
  DropdownMenu as SubframeDropdownMenu,
} from "@subframe/core";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  MatchRoute,
  notFound,
  Outlet,
  redirect,
  useMatchRoute,
} from "@tanstack/react-router";
import { ReactNode, useCallback, useMemo, useRef } from "react";
import { z } from "zod";
import { Trans, useLingui } from "@lingui/react/macro";
import { PublishedProvider } from "@op/utils";
import { toast } from "sonner";
import { languageKeys } from "@op/puck-types";
import { TopbarProvider } from "@/providers/TopbarProvider";

const paramSchema = z.object({
  organizationSlug: z.string(),
  projectSlug: z.string(),
  languageKey: z.enum(languageKeys),
  topicSlug: z.string(),
  pageSlug: z.string(),
  versionId: z.string(),
});

async function getLayout(versionId: string) {
  const res = await editorClient.versions[":versionId"].layout.$get({
    param: {
      versionId,
    },
  });
  if (!res.ok) {
    if (res.status === 401) throw redirect({ to: "/login" });
    if (res.status === 404) throw notFound();
    throw new Error("Failed to get layout");
  }
  const layout = await res.json();
  return layout;
}

export const Route = createFileRoute(
  "/app/$organizationSlug/$projectSlug/$languageKey/$topicSlug/$pageSlug/$versionId",
)({
  component: RouteComponent,
  params: paramSchema,
  beforeLoad: ({ params }) => ({
    getLayoutQuery: {
      queryKey: ["editor-layout", { versionId: params.versionId }],
      queryFn: async () => await getLayout(params.versionId),
    },
  }),
  loader: async ({ context: { queryClient, getLayoutQuery } }) =>
    await queryClient.ensureQueryData(getLayoutQuery),
});

function RouteComponent() {
  const {
    versionId,
    organizationSlug,
    projectSlug,
    languageKey,
    pageSlug,
    topicSlug,
  } = Route.useParams();
  const savedBadgeRef = useRef<HTMLDivElement>(null);
  const undoRedoRef = useRef<HTMLDivElement>(null);

  const { getLayoutQuery } = Route.useRouteContext();

  const { t } = useLingui();

  const queryClient = useQueryClient();

  const { data: layout } = useSuspenseQuery(getLayoutQuery, queryClient);

  const publishVersion = useCallback(async () => {
    const res = await editorClient.versions[":versionId"].publish.$post({
      param: {
        versionId,
      },
    });
    if (!res.ok) {
      throw new Error("Failed to publish version");
    }
  }, [versionId]);

  const publishMutation = useMutation({
    mutationFn: async () => await publishVersion(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "editor-topic-pages",
          { organizationSlug, projectSlug, languageKey, topicSlug },
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["editor-layout", { versionId }],
      });
      toast.success(t`Version published`);
    },
    onError: () => {
      toast.error(t`Failed to publish version`, {
        description: t`Please try again later`,
      });
    },
  });

  const isChanged = useMemo(() => layout.changed, [layout]);
  const isPublished = useMemo(() => layout.publishedAt !== null, [layout]);

  const previewUrl = useMemo(
    () => new URL(`/preview/${versionId}`, layout.rendererUrl).toString(),
    [versionId, layout.rendererUrl],
  );

  const publishButtonLoading = useMemo(
    () => publishMutation.isPending,
    [publishMutation],
  );

  const publishButtonDisabled = useMemo(
    () => isPublished || publishButtonLoading || !isChanged,
    [isPublished, publishButtonLoading, isChanged],
  );

  const tabNames: Record<string, string> = {
    content: t`Content`,
    excursions: t`Excursions`,
    glossary: t`Glossary`,
    citations: t`Citations`,
    media: t`Media`,
  } as const;

  const tabIcons: Record<string, ReactNode> = {
    content: <FeatherText />,
    excursions: <FeatherArrowUpRight />,
    glossary: <FeatherBookA />,
    citations: <FeatherBookmark />,
    media: <FeatherImage />,
  };

  const matchRoute = useMatchRoute();
  const params = matchRoute({
    to: "/app/$organizationSlug/$projectSlug/$languageKey/$topicSlug/$pageSlug/$versionId",
    fuzzy: true,
  });

  const tabName = useMemo(() => (params as any)["**"] ?? "content", [params]);

  return (
    <div className="w-full flex flex-col gap-2 mt-4">
      <div className="w-full mx-auto max-w-[1280px] px-4">
        <EditorTopbar
          tabSlot={
            <SubframeDropdownMenu.Root>
              <SubframeDropdownMenu.Trigger asChild={true}>
                <Button
                  disabled={false}
                  variant="neutral-primary"
                  size="medium"
                  icon={tabIcons[tabName] ?? <FeatherArrowUpRight />}
                  iconRight={<FeatherChevronDown />}
                  loading={false}
                >
                  {tabNames[tabName] ?? <Trans>Editing Excursion</Trans>}
                </Button>
              </SubframeDropdownMenu.Trigger>
              <SubframeDropdownMenu.Portal>
                <SubframeDropdownMenu.Content
                  side="bottom"
                  align="start"
                  sideOffset={4}
                  asChild={true}
                >
                  <DropdownMenu>
                    <MatchRoute to="/app/$organizationSlug/$projectSlug/$languageKey/$topicSlug/$pageSlug/$versionId/content">
                      {(match) =>
                        match ? (
                          <DropdownMenu.DropdownItem
                            icon={
                              <span className="text-brand-700 contents">
                                {tabIcons.content}
                              </span>
                            }
                          >
                            <span className="text-brand-700">
                              {tabNames.content}
                            </span>
                          </DropdownMenu.DropdownItem>
                        ) : (
                          <Link
                            to="/app/$organizationSlug/$projectSlug/$languageKey/$topicSlug/$pageSlug/$versionId/content"
                            params={{
                              organizationSlug,
                              projectSlug,
                              languageKey,
                              topicSlug,
                              pageSlug,
                              versionId,
                            }}
                            className="contents"
                          >
                            <DropdownMenu.DropdownItem icon={tabIcons.content}>
                              {tabNames.content}
                            </DropdownMenu.DropdownItem>
                          </Link>
                        )
                      }
                    </MatchRoute>
                    <MatchRoute to="/app/$organizationSlug/$projectSlug/$languageKey/$topicSlug/$pageSlug/$versionId/excursions">
                      {(match) =>
                        match ? (
                          <DropdownMenu.DropdownItem
                            icon={
                              <span className="text-brand-700 contents">
                                {tabIcons.excursions}
                              </span>
                            }
                          >
                            <span className="text-brand-700">
                              {tabNames.excursions}
                            </span>
                          </DropdownMenu.DropdownItem>
                        ) : (
                          <Link
                            to="/app/$organizationSlug/$projectSlug/$languageKey/$topicSlug/$pageSlug/$versionId/excursions"
                            params={{
                              organizationSlug,
                              projectSlug,
                              languageKey,
                              topicSlug,
                              pageSlug,
                              versionId,
                            }}
                            className="contents"
                          >
                            <DropdownMenu.DropdownItem
                              icon={tabIcons.excursions}
                            >
                              {tabNames.excursions}
                            </DropdownMenu.DropdownItem>
                          </Link>
                        )
                      }
                    </MatchRoute>
                    <MatchRoute to="/app/$organizationSlug/$projectSlug/$languageKey/$topicSlug/$pageSlug/$versionId/glossary">
                      {(match) =>
                        match ? (
                          <DropdownMenu.DropdownItem
                            icon={
                              <span className="text-brand-700 contents">
                                {tabIcons.glossary}
                              </span>
                            }
                          >
                            <span className="text-brand-700">
                              {tabNames.glossary}
                            </span>
                          </DropdownMenu.DropdownItem>
                        ) : (
                          <Link
                            to="/app/$organizationSlug/$projectSlug/$languageKey/$topicSlug/$pageSlug/$versionId/glossary"
                            params={{
                              organizationSlug,
                              projectSlug,
                              languageKey,
                              topicSlug,
                              pageSlug,
                              versionId,
                            }}
                            className="contents"
                          >
                            <DropdownMenu.DropdownItem icon={tabIcons.glossary}>
                              {tabNames.glossary}
                            </DropdownMenu.DropdownItem>
                          </Link>
                        )
                      }
                    </MatchRoute>
                    <MatchRoute to="/app/$organizationSlug/$projectSlug/$languageKey/$topicSlug/$pageSlug/$versionId/citations">
                      {(match) =>
                        match ? (
                          <DropdownMenu.DropdownItem
                            icon={
                              <span className="text-brand-700 contents">
                                {tabIcons.citations}
                              </span>
                            }
                          >
                            <span className="text-brand-700">
                              {tabNames.citations}
                            </span>
                          </DropdownMenu.DropdownItem>
                        ) : (
                          <Link
                            to="/app/$organizationSlug/$projectSlug/$languageKey/$topicSlug/$pageSlug/$versionId/citations"
                            params={{
                              organizationSlug,
                              projectSlug,
                              languageKey,
                              topicSlug,
                              pageSlug,
                              versionId,
                            }}
                            className="contents"
                          >
                            <DropdownMenu.DropdownItem
                              icon={tabIcons.citations}
                            >
                              {tabNames.citations}
                            </DropdownMenu.DropdownItem>
                          </Link>
                        )
                      }
                    </MatchRoute>
                    <MatchRoute to="/app/$organizationSlug/$projectSlug/$languageKey/$topicSlug/$pageSlug/$versionId/media">
                      {(match) =>
                        match ? (
                          <DropdownMenu.DropdownItem
                            icon={
                              <span className="text-brand-700 contents">
                                {tabIcons.media}
                              </span>
                            }
                          >
                            <span className="text-brand-700">
                              {tabNames.media}
                            </span>
                          </DropdownMenu.DropdownItem>
                        ) : (
                          <Link
                            to="/app/$organizationSlug/$projectSlug/$languageKey/$topicSlug/$pageSlug/$versionId/media"
                            params={{
                              organizationSlug,
                              projectSlug,
                              languageKey,
                              topicSlug,
                              pageSlug,
                              versionId,
                            }}
                            className="contents"
                          >
                            <DropdownMenu.DropdownItem icon={tabIcons.media}>
                              {tabNames.media}
                            </DropdownMenu.DropdownItem>
                          </Link>
                        )
                      }
                    </MatchRoute>
                  </DropdownMenu>
                </SubframeDropdownMenu.Content>
              </SubframeDropdownMenu.Portal>
            </SubframeDropdownMenu.Root>
          }
          savedBadgeSlot={<div ref={savedBadgeRef} className="contents" />}
          undoRedoSlot={<div ref={undoRedoRef} className="contents" />}
          actionSlot={
            <>
              <a href={previewUrl} target="_blank" className="contents">
                <Button variant="neutral-tertiary">
                  <Trans>Preview draft</Trans>
                </Button>
              </a>
              <Link
                to="/app/$organizationSlug/$projectSlug/$languageKey/$topicSlug/$pageSlug/settings"
                params={{
                  organizationSlug,
                  projectSlug,
                  languageKey,
                  pageSlug,
                  topicSlug,
                }}
                className="contents"
              >
                <IconButton
                  disabled={false}
                  variant="neutral-primary"
                  size="medium"
                  icon={<FeatherSettings />}
                  loading={false}
                />
              </Link>
              <Button
                variant="brand-secondary"
                loading={publishButtonLoading}
                disabled={publishButtonDisabled}
                onClick={() => publishMutation.mutateAsync()}
              >
                <Trans>Publish</Trans>
              </Button>
            </>
          }
        />
      </div>
      <PublishedProvider published={isPublished}>
        <TopbarProvider savedBadgeRef={savedBadgeRef} undoRedoRef={undoRedoRef}>
          <Outlet />
        </TopbarProvider>
      </PublishedProvider>
      {isPublished && (
        <div className="fixed bottom-8 left-8 right-8 mx-auto flex justify-center">
          <div>
            <Alert
              variant="warning"
              icon={<FeatherInfo />}
              title={t`You're viewing a published page`}
              description={t`Published pages are read-only. If you'd like to edit the content for this page, please switch to the latest draft.`}
              actions={
                <Link
                  to="/app/$organizationSlug/$projectSlug/$languageKey/$topicSlug/$pageSlug/$versionId"
                  params={{
                    versionId: layout.newestDraftId!,
                    organizationSlug,
                    projectSlug,
                    languageKey,
                    pageSlug,
                    topicSlug,
                  }}
                >
                  <Button
                    disabled={false}
                    variant="neutral-tertiary"
                    size="small"
                    icon={null}
                    iconRight={<FeatherArrowRight />}
                    loading={false}
                  >
                    <Trans>Latest draft</Trans>
                  </Button>
                </Link>
              }
            />
          </div>
        </div>
      )}
    </div>
  );
}
